/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Module containing theme components
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.0.0
 */
import assign from 'lodash/assign';

import colorsDefault from './default/colors';
import * as paletteDefault from './default/colors/palette';
import * as accountSelectDefault from './default/components/accountSelect';
import * as buttonDefault from './default/components/button';
import * as checkboxDefault from './default/components/checkbox';
import * as dashboardDefault from './default/components/dashboard';
import * as inputDefault from './default/components/input';
import * as logoContainerDefault from './default/components/logoContainer';
import * as multiSelectDefault from './default/components/multiSelect';
import * as noticeDefault from './default/components/notice';
import * as selectDefault from './default/components/select';
import * as selectItemDefault from './default/components/selectItem';
import * as tableDefault from './default/components/table';
import * as tabMenuDefault from './default/components/tabMenu';
import * as wizardDefault from './default/components/wizard';
import * as fontsDefault from './default/fonts';
import * as globalThemeConstantsDefault from './default/global';
import * as iconsDefault from './default/icons';
import * as loaderDefault from './default/loader';

if (!window.theme) {
    require('./default/fonts.css');
}

let externalTheme: any;

try {
    externalTheme = require('./custom');
} catch (err) {
    if (process.env.NODE_ENV === 'development') {
        console.error(err);
    }
}

const defaultTheme = {
    accountSelect: accountSelectDefault,
    button: buttonDefault,
    checkbox: checkboxDefault,
    colors: colorsDefault,
    dashboard: dashboardDefault,
    fonts: fontsDefault,
    globalThemeConstants: globalThemeConstantsDefault,
    icons: iconsDefault,
    input: inputDefault,
    loader: loaderDefault,
    logoContainer: logoContainerDefault,
    multiSelect: multiSelectDefault,
    notice: noticeDefault,
    select: selectDefault,
    selectItem: selectItemDefault,
    tabMenu: tabMenuDefault,
    table: tableDefault,
    wizard: wizardDefault,
    palette: paletteDefault,
};

const theme = window.theme ? window.theme() : externalTheme ? externalTheme() : {};

const accountSelect = assign(defaultTheme.accountSelect, theme.accountSelect);
const button = assign(defaultTheme.button, theme.button);
const checkbox = assign(defaultTheme.checkbox, theme.checkbox);
const dashboard = assign(defaultTheme.dashboard, theme.dashboard);
const colors = assign(defaultTheme.colors, theme.colors);
const fonts = assign(defaultTheme.fonts, theme.fonts);
const globalThemeConstants = assign(defaultTheme.globalThemeConstants, theme.globalThemeConstants);
const icons = theme.icons ?? defaultTheme.icons;
const input = assign(defaultTheme.input, theme.input);
const loader = assign(defaultTheme.loader, theme.loader);
const logoContainer = assign(defaultTheme.logoContainer, theme.logoContainer);
const multiSelect = assign(defaultTheme.multiSelect, theme.multiSelect);
const notice = assign(defaultTheme.notice, theme.notice);
const select = assign(defaultTheme.select, theme.select);
const selectItem = assign(defaultTheme.selectItem, theme.selectItem);
const table = assign(defaultTheme.table, theme.table);
const tabMenu = assign(defaultTheme.tabMenu, theme.tabMenu);
const translation = theme.translation;
const wizard = assign(defaultTheme.wizard, theme.wizard);
const palette = assign(defaultTheme.palette, theme.palette);

export {
    accountSelect,
    button,
    checkbox,
    colors,
    dashboard,
    fonts,
    globalThemeConstants,
    icons,
    input,
    loader,
    logoContainer,
    multiSelect,
    notice,
    select,
    selectItem,
    table,
    tabMenu,
    translation,
    wizard,
    palette,
    defaultTheme,
};
